import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { format } from 'date-fns';
import { getTotalData, api } from '../services/api';
import '../styles/TotalDataTable.css';
import SnapshotCalendarSelector from '../components/SnapshotCalendarSelector';

// Helper function to format currency
const formatCurrency = (value) => {
  if (!value || value === 0) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0
  }).format(value);
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd/MM/yy');
  } catch {
    return dateString;
  }
};

// Define table columns with their properties - optimized for better display
const columns = [
  { id: 'Brand', label: 'Brand', minWidth: 120, width: 120, align: 'left', sticky: true, resizable: true },
  { id: 'Sub_Brand', label: 'Sub Brand', minWidth: 120, width: 120, align: 'left', resizable: true },
  { id: 'Campaign_Name', label: 'Campaign Name', minWidth: 200, width: 200, align: 'left', priority: 'high', resizable: true },
  { id: 'Campaign_Type', label: 'Type', minWidth: 100, width: 100, align: 'left', resizable: true },
  { id: 'Objective_KPI', label: 'Objective', minWidth: 120, width: 120, align: 'left', resizable: true },
  { id: 'Media_Type_Channel', label: 'Media', minWidth: 100, width: 100, align: 'left', priority: 'high', resizable: true },
  { id: 'Sub_media_type', label: 'Sub Media', minWidth: 120, width: 120, align: 'left', resizable: true },
  { id: 'Digital_Vendor', label: 'Vendor', minWidth: 120, width: 120, align: 'left', resizable: true },
  { id: 'Start_Date', label: 'Start', minWidth: 100, width: 100, align: 'center', format: formatDate, resizable: true },
  { id: 'End_Date', label: 'End', minWidth: 100, width: 100, align: 'center', format: formatDate, resizable: true },
  { id: 'Month', label: 'Month', minWidth: 100, width: 100, align: 'center', format: formatDate, priority: 'high', resizable: true },
  { id: 'Given_Cost_Excl_VAT_VND', label: 'Given', minWidth: 120, width: 120, align: 'right', format: formatCurrency, priority: 'high', resizable: true },
  { id: 'Planning_Gross_Cost_Excl_VAT_VND', label: 'Planning', minWidth: 120, width: 120, align: 'right', format: formatCurrency, priority: 'high', resizable: true },
  { id: 'Actual_Gross_Cost_Excl_VAT_VND', label: 'Actual', minWidth: 120, width: 120, align: 'right', format: formatCurrency, priority: 'high', resizable: true },
  { id: 'Net_Net_cost_Excl_tech_vat_withholding_tax_tracking_VND', label: 'Net Net', minWidth: 120, width: 120, align: 'right', format: formatCurrency, resizable: true },
  { id: 'Tracking_Fees_DCM', label: 'Tracking', minWidth: 100, width: 100, align: 'right', format: formatCurrency, resizable: true },
  { id: 'WHTax', label: 'WHT', minWidth: 100, width: 100, align: 'right', format: formatCurrency, resizable: true },
  { id: 'PO_number', label: 'PO #', minWidth: 100, width: 100, align: 'left', resizable: true },
  { id: 'PO_cost', label: 'PO Cost', minWidth: 120, width: 120, align: 'right', format: formatCurrency, resizable: true },
  { id: 'Status_Planning_Ongoing_Done', label: 'Status', minWidth: 100, width: 100, align: 'center', priority: 'medium', resizable: true },
  { id: 'NTW', label: 'NTW', minWidth: 100, width: 100, align: 'right', format: formatCurrency, group: 'regions', resizable: true },
  { id: 'Central_Mid', label: 'C.Mid', minWidth: 100, width: 100, align: 'right', format: formatCurrency, group: 'regions', resizable: true },
  { id: 'The_rest_of_Central', label: 'C.Rest', minWidth: 100, width: 100, align: 'right', format: formatCurrency, group: 'regions', resizable: true },
  { id: 'GHCM', label: 'GHCM', minWidth: 100, width: 100, align: 'right', format: formatCurrency, group: 'regions', resizable: true },
  { id: 'Mekong', label: 'Mekong', minWidth: 100, width: 100, align: 'right', format: formatCurrency, group: 'regions', resizable: true },
  { id: 'North', label: 'North', minWidth: 100, width: 100, align: 'right', format: formatCurrency, group: 'regions', resizable: true },
];

// Column Resizer Component
const ColumnResizer = ({ onResize, columnId }) => {
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e) => {
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = e.target.parentElement.offsetWidth;
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(80, startWidth.current + diff);
    onResize(columnId, newWidth);
  }, [isResizing, onResize, columnId]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <Box
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        cursor: 'col-resize',
        backgroundColor: isResizing ? 'primary.main' : 'transparent',
        '&:hover': {
          backgroundColor: 'primary.light',
        },
        zIndex: 10,
      }}
      onMouseDown={handleMouseDown}
    />
  );
};

const TotalData = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filter states
  const [selectedSnapshotMonth, setSelectedSnapshotMonth] = useState('');
  const [snapshotMonths, setSnapshotMonths] = useState([]);
  const [brandFilter, setBrandFilter] = useState('all');
  const [snapshotInfo, setSnapshotInfo] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Column width state
  const [columnWidths, setColumnWidths] = useState(() => {
    const initialWidths = {};
    columns.forEach(col => {
      initialWidths[col.id] = col.width || col.minWidth;
    });
    return initialWidths;
  });

  // Handle column resize
  const handleColumnResize = useCallback((columnId, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth
    }));
  }, []);

  // Initialize filters from URL params after snapshot months are loaded
  useEffect(() => {
    // Only initialize from URL when snapshot months are loaded (to avoid out-of-range warnings)
    if (snapshotMonths.length > 0 || !snapshotLoading) {
      const snapshotMonth = searchParams.get('snapshotMonth');
      const brand = searchParams.get('brand');
      
      if (snapshotMonth) setSelectedSnapshotMonth(snapshotMonth);
      if (brand) setBrandFilter(brand);
    }
  }, [searchParams, snapshotMonths, snapshotLoading]);

  // Fetch snapshot months
  const fetchSnapshotMonths = useCallback(async () => {
    try {
      setSnapshotLoading(true);
      const response = await api.get('/system/snapshot/months/');
      setSnapshotMonths(response.data.months || []);
    } catch (err) {
      console.error('Error fetching snapshot months:', err);
      setSnapshotMonths([]);
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  // Fetch total data
  const fetchTotalData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching total data with params:', {
        page: page + 1,
        page_size: rowsPerPage,
        snapshot_month: selectedSnapshotMonth,
        brand: brandFilter !== 'all' ? brandFilter : undefined
      });
      
      const response = await getTotalData({
        page: page + 1,
        page_size: rowsPerPage,
        ...(selectedSnapshotMonth && { snapshot_month: selectedSnapshotMonth }),
        ...(brandFilter !== 'all' && { brand: brandFilter }),
      });
      const result = response.data;
      
      setData(result.data || []);
      setTotalCount(result.total_count || 0);
      
      // Debug logging
      console.log('Total data response:', {
        dataCount: result.data?.length || 0,
        totalCount: result.total_count || 0,
        isSnapshot: result.is_snapshot,
        snapshotMonth: result.snapshot_month
      });
      
      // Set snapshot info if viewing snapshot data
      if (result.is_snapshot) {
        setSnapshotInfo({
          month: result.snapshot_month,
          isSnapshot: true
        });
      } else {
        setSnapshotInfo(null);
      }
      
    } catch (err) {
      console.error('Error fetching total data:', err);
      
      // More detailed error handling
      let errorMessage = 'Error loading total data';
      
      if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        const data = err.response.data;
        
        console.error('Server error details:', {
          status,
          data,
          url: err.config?.url,
          params: err.config?.params
        });
        
        if (status === 500) {
          errorMessage = `Server error (500): ${data?.error || 'Internal server error'}`;
          if (selectedSnapshotMonth) {
            errorMessage += `. There may be an issue with snapshot data for ${selectedSnapshotMonth}.`;
          }
        } else if (status === 404) {
          errorMessage = `Data not found (404): ${data?.error || 'No data available'}`;
          if (selectedSnapshotMonth) {
            errorMessage = `No snapshot data found for ${selectedSnapshotMonth}`;
          }
        } else if (status === 400) {
          errorMessage = `Bad request (400): ${data?.error || 'Invalid request parameters'}`;
        } else {
          errorMessage = `Server error (${status}): ${data?.error || err.message}`;
        }
      } else if (err.request) {
        // Network error
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        // Other error
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      setData([]);
      setTotalCount(0);
      setSnapshotInfo(null);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedSnapshotMonth, brandFilter]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchSnapshotMonths();
  }, [fetchSnapshotMonths]);

  useEffect(() => {
    fetchTotalData();
  }, [fetchTotalData]);

  // Handle snapshot month change
  const handleSnapshotMonthChange = (event) => {
    const month = event.target.value === 'live' ? null : event.target.value;
    setSelectedSnapshotMonth(month);
    setPage(0); // Reset pagination
    
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    if (month) {
      newSearchParams.set('snapshotMonth', month);
    } else {
      newSearchParams.delete('snapshotMonth');
    }
    setSearchParams(newSearchParams);
    
    // Log for debugging
    console.log(`Switched to ${month ? 'snapshot data for ' + month : 'live data'}`);
  };

  // Handle brand filter change
  const handleBrandFilterChange = (event) => {
    const brand = event.target.value;
    setBrandFilter(brand);
    setPage(0); // Reset pagination
    
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    if (brand !== 'all') {
      newSearchParams.set('brand', brand);
    } else {
      newSearchParams.delete('brand');
    }
    setSearchParams(newSearchParams);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchTotalData();
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedSnapshotMonth('');
    setBrandFilter('all');
    setPage(0);
    setSearchParams(new URLSearchParams());
  };

  // Export data (placeholder for future implementation)
  const handleExport = () => {
    // TODO: Implement CSV export functionality
    console.log('Export functionality to be implemented');
  };

  // Get unique brands from data for filter options
  const availableBrands = React.useMemo(() => {
    const brands = new Set(data.map(row => row.Brand).filter(Boolean));
    return ['all', ...Array.from(brands).sort()];
  }, [data]);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <Container maxWidth="xl" sx={{ height: '100vh', overflow: 'hidden', py: { xs: 1, sm: 2 } }}>
      <Box sx={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: 2, maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  Total Data Overview
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  {snapshotInfo ? (
                    <>
                      <Chip 
                        icon={<HistoryIcon fontSize="small" />}
                        label={`Snapshot: ${format(new Date(snapshotInfo.month + '-01'), 'MMM yyyy')}`}
                        color="warning" 
                        size="small" 
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary">
                        View-only historical data
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Chip 
                        icon={<RefreshIcon fontSize="small" />}
                        label="Live Data" 
                        color="success" 
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Real-time comprehensive view
                      </Typography>
                    </>
                  )}
                  
                  {/* Data summary */}
                  {!loading && (
                    <>
                      <Typography variant="body2" color="text.disabled" sx={{ mx: 1 }}>•</Typography>
                      <Chip 
                        label={`${totalCount.toLocaleString()} records`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      {brandFilter !== 'all' && (
                        <Chip 
                          label={`Brand: ${brandFilter}`}
                          size="small"
                          variant="filled"
                          color="secondary"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Export Data">
                  <IconButton onClick={handleExport} color="primary">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh Data">
                  <IconButton onClick={handleRefresh} color="primary">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <SnapshotCalendarSelector
                availableSnapshots={snapshotMonths}
                selectedSnapshot={selectedSnapshotMonth}
                onSnapshotSelect={handleSnapshotMonthChange}
                onLiveDataSelect={() => handleSnapshotMonthChange({ target: { value: 'live' } })}
                loading={snapshotLoading}
                sx={{ minWidth: 220 }}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="brand-filter-label">Brand Filter</InputLabel>
                <Select
                  labelId="brand-filter-label"
                  value={brandFilter}
                  label="Brand Filter"
                  onChange={handleBrandFilterChange}
                >
                  <MenuItem value="all">
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        All Brands
                      </Typography>
                    </Box>
                  </MenuItem>
                  
                  {availableBrands.length > 1 && (
                    <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        AVAILABLE BRANDS ({availableBrands.length - 1})
                      </Typography>
                    </Box>
                  )}
                  
                  {availableBrands.filter(brand => brand !== 'all').map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      <Typography variant="body2">{brand}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(selectedSnapshotMonth || brandFilter !== 'all') && (
                <Button
                  startIcon={<CloseIcon />}
                  onClick={clearFilters}
                  size="small"
                  color="secondary"
                >
                  Clear Filters
                </Button>
              )}
            </Box>

            {/* Active filters display */}
            {(selectedSnapshotMonth || brandFilter !== 'all') && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedSnapshotMonth && (
                  <Chip
                    icon={<HistoryIcon fontSize="small" />}
                    label={`Snapshot: ${selectedSnapshotMonth}`}
                    onDelete={() => setSelectedSnapshotMonth('')}
                    color="secondary"
                    size="small"
                  />
                )}
                {brandFilter !== 'all' && (
                  <Chip
                    label={`Brand: ${brandFilter}`}
                    onDelete={() => setBrandFilter('all')}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            )}
          </Paper>

          {/* Enhanced Data Table Container */}
          <Paper elevation={2} sx={{ 
            borderRadius: 2, 
            overflow: 'hidden', 
            border: '1px solid rgba(0,0,0,0.08)',
            flex: 1,
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
            width: '100%'
          }}>
            {error && (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light', borderRadius: 1, m: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'error.dark', mb: 1 }}>
                  {error}
                </Typography>
                {selectedSnapshotMonth && error.includes('500') && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      There seems to be an issue with the snapshot data. You can try:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => {
                          setSelectedSnapshotMonth('');
                          setError(null);
                        }}
                        startIcon={<RefreshIcon />}
                      >
                        Switch to Live Data
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={handleRefresh}
                        startIcon={<RefreshIcon />}
                      >
                        Retry
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Loading total data...</Typography>
              </Box>
            ) : (
              <>
                {/* Scrollable Table Container */}
                <Box 
                  className="total-data-scrollable"
                  sx={{ 
                    flex: 1,
                    overflow: 'auto',
                    position: 'relative',
                    width: '100%',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': {
                      width: '12px',
                      height: '12px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#c1c1c1',
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: '#a8a8a8',
                      },
                    },
                    '&::-webkit-scrollbar-corner': {
                      backgroundColor: '#f1f1f1',
                    },
                  }}
                >
                  <Table 
                    className="total-data-table"
                    stickyHeader 
                    size="small" 
                    sx={{ 
                      minWidth: Math.max(1200, Object.values(columnWidths).reduce((sum, width) => sum + width, 0)),
                      tableLayout: 'fixed',
                      width: 'max-content'
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{ 
                              width: columnWidths[column.id],
                              minWidth: column.minWidth,
                              position: column.sticky ? 'sticky' : 'static',
                              left: column.sticky ? 0 : 'auto',
                              zIndex: column.sticky ? 1001 : 1000,
                            }}
                            sx={{
                              backgroundColor: column.sticky 
                                ? theme.palette.background.paper
                                : column.group === 'regions' 
                                ? alpha(theme.palette.warning.main, 0.1)
                                : column.priority === 'high'
                                ? alpha(theme.palette.primary.main, 0.15)
                                : alpha(theme.palette.primary.main, 0.08),
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              py: 1.5,
                              px: 1,
                              borderRight: column.sticky ? '2px solid rgba(0,0,0,0.12)' : '1px solid rgba(0,0,0,0.06)',
                              borderBottom: '2px solid rgba(0,0,0,0.12)',
                              color: column.group === 'regions' ? theme.palette.warning.dark : theme.palette.primary.dark,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              position: 'relative',
                              userSelect: 'none',
                              '&:hover .column-resizer': {
                                opacity: 1,
                              }
                            }}
                          >
                            <Box sx={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              pr: column.resizable ? 1 : 0
                            }}>
                              {column.label}
                            </Box>
                            {column.resizable && (
                              <Box className="column-resizer" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
                                <ColumnResizer 
                                  onResize={handleColumnResize} 
                                  columnId={column.id} 
                                />
                              </Box>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((row, index) => (
                        <TableRow 
                          hover 
                          key={index} 
                          sx={{ 
                            '&:nth-of-type(odd)': { 
                              bgcolor: alpha(theme.palette.action.hover, 0.03) 
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              '& .MuiTableCell-root': {
                                borderColor: alpha(theme.palette.primary.main, 0.2),
                              }
                            },
                            cursor: 'default'
                          }}
                        >
                          {columns.map((column) => {
                            const value = row[column.id];
                            const formattedValue = column.format ? column.format(value) : value;
                            const isNumeric = column.format === formatCurrency;
                            const isEmpty = !value || value === 0;
                            
                            return (
                              <TableCell 
                                key={column.id} 
                                align={column.align} 
                                style={{
                                  width: columnWidths[column.id],
                                  position: column.sticky ? 'sticky' : 'static',
                                  left: column.sticky ? 0 : 'auto',
                                  zIndex: column.sticky ? 999 : 'auto',
                                }}
                                sx={{ 
                                  fontSize: '0.75rem', 
                                  py: 1, 
                                  px: 1,
                                  borderRight: column.sticky ? '2px solid rgba(0,0,0,0.12)' : '1px solid rgba(0,0,0,0.06)',
                                  backgroundColor: column.sticky ? theme.palette.background.paper : 'transparent',
                                  fontWeight: column.priority === 'high' ? 500 : 400,
                                  color: isEmpty && isNumeric 
                                    ? 'text.disabled' 
                                    : column.group === 'regions' && !isEmpty
                                    ? theme.palette.warning.dark
                                    : 'text.primary',
                                  fontFamily: isNumeric ? 'monospace' : 'inherit',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={formattedValue || value || '-'}
                              >
                                {isEmpty && isNumeric ? '-' : formattedValue || '-'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                      {data.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                                No data available
                              </Typography>
                              <Typography variant="body2" color="text.disabled">
                                {selectedSnapshotMonth ? 'Try selecting a different snapshot or switch to live data' : 'Try adjusting your filters or check back later'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>

                <TablePagination
                  rowsPerPageOptions={[25, 50, 100, 200]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`} records`
                  }
                  sx={{
                    borderTop: '2px solid',
                    borderColor: 'divider',
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiTablePagination-toolbar': {
                      minHeight: 56,
                      px: 2,
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                    },
                    '& .MuiTablePagination-select': {
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    },
                    '& .MuiIconButton-root': {
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                      '&.Mui-disabled': {
                        color: 'text.disabled',
                      },
                    },
                  }}
                />
              </>
            )}
          </Paper>
        </motion.div>
      </Box>
    </Container>
  );
};

export default TotalData; 